import { ApiError } from '@/lib/errors';
import { z } from 'zod';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';
const REQUEST_TIMEOUT = 30000; // 30 seconds

export const getAuthToken = (): string | null => {
  return localStorage.getItem('idToken');
};

export const setAuthToken = (token: string): void => {
  localStorage.setItem('idToken', token);
};

export const removeAuthToken = (): void => {
  localStorage.removeItem('idToken');
};

const handleUnauthorized = (): void => {
  removeAuthToken();
  localStorage.removeItem('user');
  window.location.href = '/login';
};

const getHeaders = (): HeadersInit => {
  const token = getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('Unauthorized', 401);
  }
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new ApiError(
      `Request failed with status ${response.status}`,
      response.status,
      undefined,
      detail.trim() || undefined
    );
  }
  return response.json();
}

async function handleEmptyResponse(response: Response): Promise<void> {
  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('Unauthorized', 401);
  }
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new ApiError(
      `Request failed with status ${response.status}`,
      response.status,
      undefined,
      detail.trim() || undefined
    );
  }
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeout: number = REQUEST_TIMEOUT
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError('Request timeout', 408);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export const apiClient = {
  async get<T>(endpoint: string, schema?: z.ZodType<T, z.ZodTypeDef, unknown>): Promise<T> {
    const response = await fetchWithTimeout(`${API_URL}${endpoint}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    const data = await handleResponse<T>(response);
    if (schema) {
      return schema.parse(data);
    }
    return data;
  },

  async post<T>(endpoint: string, data: unknown, schema?: z.ZodType<T, z.ZodTypeDef, unknown>): Promise<T> {
    const response = await fetchWithTimeout(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    const result = await handleResponse<T>(response);
    if (schema) {
      return schema.parse(result);
    }
    return result;
  },

  async put<T>(endpoint: string, data: unknown, schema?: z.ZodType<T, z.ZodTypeDef, unknown>): Promise<T> {
    const response = await fetchWithTimeout(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    const result = await handleResponse<T>(response);
    if (schema) {
      return schema.parse(result);
    }
    return result;
  },

  async postRaw<T>(endpoint: string, body: BodyInit, contentType: string, schema?: z.ZodType<T, z.ZodTypeDef, unknown>): Promise<T> {
    const token = getAuthToken();
    const headers: HeadersInit = { 'Content-Type': contentType };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetchWithTimeout(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body,
    });
    const result = await handleResponse<T>(response);
    if (schema) {
      return schema.parse(result);
    }
    return result;
  },

  async delete(endpoint: string): Promise<void> {
    const response = await fetchWithTimeout(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    await handleEmptyResponse(response);
  },
};
